import type { ModuleDescriptor } from "src/app/moduleDescriptor";

/** One `.module.tsx` file's exports: export name -> value. */
export type ModuleFileExports = Record<string, unknown>;

/** `import.meta.glob(..., { eager: true })` output: file path -> that file's exports. */
export type ModuleFiles = Record<string, ModuleFileExports>;

function isModuleDescriptor(value: unknown): value is ModuleDescriptor {
  if (typeof value !== "object" || value === null) return false;
  const { id, path, order } = value as { id?: unknown; path?: unknown; order?: unknown };
  return (
    typeof id === "string" &&
    id.length > 0 &&
    typeof path === "string" &&
    path.startsWith("/") &&
    (order === undefined || typeof order === "number") &&
    "element" in value
  );
}

// Code-unit order, not locale order, so the result is the same on every machine.
function compareText(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function assertUnique(registry: readonly ModuleDescriptor[], key: "id" | "path"): void {
  const seen = new Set<string>();
  for (const module of registry) {
    if (seen.has(module[key])) throw new Error(`Two modules register the ${key} "${module[key]}".`);
    seen.add(module[key]);
  }
}

/**
 * The registry: every descriptor exported by a feature's `<feature>.module.tsx`
 * (by file path, then export name), then the app-level pages, sorted by `order`
 * with unordered modules last. A malformed export, a duplicate `id` or `path`,
 * or an empty `requiredEntitlements` throws, so a mistake fails at startup rather than as a missing page.
 */
export function buildModuleRegistry(
  featureFiles: ModuleFiles,
  appPages: readonly ModuleDescriptor[]
): ModuleDescriptor[] {
  const featureModules = Object.keys(featureFiles)
    .sort(compareText)
    .flatMap((file) =>
      Object.entries(featureFiles[file])
        .sort(([a], [b]) => compareText(a, b))
        .map(([exportName, value]) => {
          if (!isModuleDescriptor(value)) {
            throw new Error(`${file} export "${exportName}" is not a ModuleDescriptor.`);
          }
          return value;
        })
    );

  // Array.prototype.sort is stable, so modules with the same order keep registry order.
  const registry = [...featureModules, ...appPages].sort(
    (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity) || 0
  );
  assertUnique(registry, "id");
  assertUnique(registry, "path");
  for (const module of registry) {
    if (module.requiredEntitlements?.length === 0) {
      throw new Error(
        `Module "${module.id}" has an empty requiredEntitlements; omit it for any signed-in user.`
      );
    }
  }
  return registry;
}
